/**
 * ImportListonePage — admin upload listone giocatori Fantagazzetta (FASE 7).
 *
 * 1 caricamento a stagione. Excel .xlsx o CSV. Formato Fantagazzetta Magic:
 *   Id | R | RM | Nome | Squadra | Qt.A | Qt.I
 */

import { useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { api } from "../../lib/api";

import { MobileShell } from "../shell/MobileShell";
import { tr } from "../../i18n";
interface ImportReport {
  righe_processate: number;
  inseriti: number;
  aggiornati: number;
  skip_duplicato: number;
  club_aggiornati: number;
  segnati_fuori: number;
  rientrati: number;
}

export function ImportListonePage() {
  useAuth(); // session check
  const [file, setFile] = useState<File | null>(null);
  const [sovrascrivi, setSovrascrivi] = useState(false);
  const [segnaAssenti, setSegnaAssenti] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  async function submit() {
    if (!file) return;
    setSubmitting(true);
    setErrore(null);
    setReport(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("sovrascrivi", sovrascrivi ? "true" : "false");
      fd.append("segna_assenti", segnaAssenti ? "true" : "false");
      const r = await api.post<ImportReport>("/admin/giocatori/import-listone", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setReport(r.data);
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setErrore(err.response?.data?.detail ?? err.message ?? "errore");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <MobileShell logoSuffix="LISTONE">
      <h2 className="font-display text-3xl text-accent tracking-wide">
        {tr("Import Listone Giocatori")}
      </h2>

      <div className="card space-y-3">
        <div>
          <label className="text-xs text-fg-muted uppercase tracking-wider">
            {tr("File listone")}
          </label>
          <input
            type="file"
            accept=".xlsx,.xlsm,.csv,.txt"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            className="w-full mt-1 px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm"
          />
          {file && (
            <p className="text-xs text-fg-muted mt-1">
              {file.name} · {(file.size / 1024).toFixed(1)} KB
            </p>
          )}
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={sovrascrivi}
            onChange={(e) => setSovrascrivi(e.target.checked)}
          />
          <span>
            {tr("Sovrascrivi ruoli/club su giocatori esistenti (altrimenti skip duplicati)")}
          </span>
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={segnaAssenti}
            onChange={(e) => setSegnaAssenti(e.target.checked)}
          />
          <span>
            Chi non è nel file → segnato{" "}
            <span className="text-warning font-bold">{tr("fuori Serie A *")}</span>{" "}
            {tr("(resta in catalogo, mai cancellato — solo per listone completo)")}
          </span>
        </label>
        <button
          onClick={submit}
          disabled={!file || submitting}
          className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? tr("Importazione in corso…") : tr("Importa listone")}
        </button>
      </div>

      {errore && (
        <div className="card bg-danger/10 border-danger/30 text-sm text-danger">{errore}</div>
      )}

      {report && (
        <div className="card space-y-2">
          <h3 className="font-bold text-accent">Report import</h3>
          <div className="grid grid-cols-4 gap-3 text-center">
            <div>
              <div className="text-xs text-fg-muted uppercase">{tr("Processate")}</div>
              <div className="font-display text-2xl text-info">{report.righe_processate}</div>
            </div>
            <div>
              <div className="text-xs text-fg-muted uppercase">{tr("Inseriti")}</div>
              <div className="font-display text-2xl text-success">{report.inseriti}</div>
            </div>
            <div>
              <div className="text-xs text-fg-muted uppercase">{tr("Aggiornati")}</div>
              <div className="font-display text-2xl text-accent">{report.aggiornati}</div>
            </div>
            <div>
              <div className="text-xs text-fg-muted uppercase">{tr("Skippati")}</div>
              <div className="font-display text-2xl text-fg-muted">{report.skip_duplicato}</div>
            </div>
            <div>
              <div className="text-xs text-fg-muted uppercase">{tr("Cambio club")}</div>
              <div className="font-display text-2xl text-info">{report.club_aggiornati}</div>
            </div>
            <div>
              <div className="text-xs text-fg-muted uppercase">{tr("Fuori SA *")}</div>
              <div className="font-display text-2xl text-warning">{report.segnati_fuori}</div>
            </div>
            <div>
              <div className="text-xs text-fg-muted uppercase">{tr("Rientrati")}</div>
              <div className="font-display text-2xl text-success">{report.rientrati}</div>
            </div>
          </div>
        </div>
      )}
    </MobileShell>
  );
}
